import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { API, BookingDTO, ClientDTO, FileLink, MediumFullDTO, PublisherPlayoutDescriptorFullDTO } from 'src/app/services/api/api';
import { SbDateTime, SbLocalDateTime } from 'src/app/services/api/api-date-time';
import { UtilService } from 'src/app/services/util.service';
import { ErrorService } from '../../../services/error.service';
import { TranslateService } from '@ngx-translate/core';
import { MsOptions, TableWrapperOptions } from '../../../services/global/global-interfaces';
import { PermissionService } from '../../../services/permission.service';
import { QueryParamService } from '../../../services/lists/query-params';
import { forkJoin, iif, of, Subscription } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ModalNavigationComponent } from '../../../services/gui/modal/modal-navigation-component';
import { DateTime } from 'luxon';
import { ExportModalOptions } from '../../../services/gui/modal/sb-export-modal/sb-export-modal.model';
import { SbExportModalComponent } from '../../../services/gui/modal/sb-export-modal/sb-export-modal.component';
import { ModalService } from '../../../services/gui/modal/modal.service';

@Component({
  selector: 'sb-page-network-bookings',
  templateUrl: './network-bookings.component.html',
  styleUrls: ['./network-bookings.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [style({ opacity: 0 }), animate(250, style({ opacity: 1 }))]),
      transition(':leave', [animate(500, style({ opacity: 0 }))]),
    ]),
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NetworkBookingsComponent implements OnInit, ModalNavigationComponent {
  private readonly modalService = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly api = inject(API);
  protected readonly util = inject(UtilService);
  protected readonly errorHandler = inject(ErrorService);
  protected readonly translate = inject(TranslateService);
  protected readonly queryParamService = inject(QueryParamService);
  protected readonly permissionService = inject(PermissionService);

  // inputs
  inDeviceContext = input<number>(null);

  // component variables
  isInModalContext: boolean;
  blockBackdrop?: boolean;
  // global variables
  options: TableWrapperOptions;
  playoutDescriptors: PublisherPlayoutDescriptorFullDTO[] = [];
  subs: Subscription[] = [];
  debounceTimer: ReturnType<typeof setTimeout> = null;
  loading = signal<boolean>(true);

  // multiselect values
  media: MediumFullDTO[] = [];
  bookings: BookingDTO[] = [];
  advertisers: ClientDTO[] = [];
  // multiselect selected values
  filteredMedia: MediumFullDTO[] = [];
  filteredBookings: BookingDTO[] = [];
  filteredAdvertisers: ClientDTO[] = [];
  // multiselect options
  msMediaOptions: MsOptions;
  msBookingsOptions: MsOptions;
  msAdvertiserOptions: MsOptions;

  // request params
  useAggregated: boolean = true;
  queryParamsParsed: boolean = false;
  productive: boolean | null = null;

  // time
  from?: SbDateTime;
  till?: SbDateTime;
  // input fields
  screen?: string;
  device?: string;

  ngOnInit() {
    this.parseQueryParams().then(() => {
      this.queryParamsParsed = true;
    });
  }

  parseQueryParams(): Promise<void> {
    return new Promise(res => {
      this.screen = this.queryParamService.getQueryParam('screen');
      this.device = this.queryParamService.getQueryParam('device');
      this.parseDateParams();
      this.parseFilterQueryParams().then(res);
    });
  }

  parseDateParams() {
    if (this.queryParamService.getQueryParam('from') && this.queryParamService.getQueryParam('till')) {
      this.from = new SbDateTime(DateTime.fromISO(this.queryParamService.getQueryParam('from')));
      this.till = new SbDateTime(DateTime.fromISO(this.queryParamService.getQueryParam('till')));
    }
  }

  parseFilterQueryParams(): Promise<void> {
    return new Promise(res => {
      const bookingsQp = this.queryParamService.getQueryParam('bookings');
      const mediaQp = this.queryParamService.getQueryParam('media');
      const advertisersQp = this.queryParamService.getQueryParam('advertisers');

      let bookingIds: number[] = [];
      let mediaIds: number[] = [];
      let advertiserIds: number[] = [];
      if (bookingsQp && bookingsQp.length > 0 && this.permissionService.hasPermission(this.permissionService.Permission.booking_list)) {
        bookingIds = JSON.parse(bookingsQp);
      }
      if (mediaQp && mediaQp.length > 0 && this.permissionService.hasPermission(this.permissionService.Permission.medium_list)) {
        mediaIds = JSON.parse(mediaQp);
      }
      if (
        advertisersQp &&
        advertisersQp.length > 0 &&
        this.permissionService.hasPermission(this.permissionService.Permission.client_user_create)
      ) {
        advertiserIds = JSON.parse(advertisersQp);
      }

      forkJoin({
        bookings: bookingIds && bookingIds.length > 0 ? this.api.ds_cm_Booking_retrieveAll({ id: bookingIds }) : of([] as BookingDTO[]),
        media: mediaIds && mediaIds.length > 0 ? this.api.mm_Medium_retrieveAll({ id: mediaIds }) : of([] as MediumFullDTO[]),
        advertisers:
          advertiserIds && advertiserIds.length > 0
            ? this.api.shared_Client_retrieveConnectedClients({ id: advertiserIds })
            : of([] as ClientDTO[]),
      })
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe(resultArray => {
          this.filteredBookings = resultArray?.bookings ?? [];
          this.filteredMedia = resultArray?.media ?? [];
          this.filteredAdvertisers = resultArray?.advertisers ?? [];
          res();
        });
    });
  }

  refreshQueryParams() {
    if (this.filteredBookings?.length > 0) {
      this.queryParamService.setQueryParam('bookings', JSON.stringify(this.util.arrayToIdArray(this.filteredBookings)));
    } else {
      this.queryParamService.clearQueryParam('bookings');
    }
    if (this.filteredMedia?.length > 0) {
      this.queryParamService.setQueryParam('media', JSON.stringify(this.util.arrayToIdArray(this.filteredMedia)));
    } else {
      this.queryParamService.clearQueryParam('media');
    }
    if (this.filteredAdvertisers?.length > 0) {
      this.queryParamService.setQueryParam('advertisers', JSON.stringify(this.util.arrayToIdArray(this.filteredAdvertisers)));
    } else {
      this.queryParamService.clearQueryParam('advertisers');
    }
    if (this.screen) {
      this.queryParamService.setQueryParam('screen', this.screen);
    } else {
      this.queryParamService.clearQueryParam('screen');
    }
    if (this.device) {
      this.queryParamService.setQueryParam('device', this.device);
    } else {
      this.queryParamService.clearQueryParam('device');
    }
    if (this.from) {
      this.queryParamService.setQueryParam('from', this.from.toString());
    } else {
      this.queryParamService.clearQueryParam('from');
    }
    if (this.till) {
      this.queryParamService.setQueryParam('till', this.till.toString());
    } else {
      this.queryParamService.clearQueryParam('till');
    }
  }

  requestDebounced(options?: TableWrapperOptions) {
    if (options) {
      this.options = options;
    }
    if (this.queryParamsParsed) {
      this.refreshQueryParams();
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.clearSubs();
    this.debounceTimer = setTimeout(() => {
      this.requestPlayoutDescriptors();
    }, 500);
  }

  clearSubs() {
    if (this.subs.length <= 0) {
      return;
    }
    this.subs.forEach(sub => {
      sub.unsubscribe();
    });
  }

  getMediaIDs() {
    return this.filteredMedia?.length > 0 ? this.filteredMedia.map(location => location.id) : null;
  }

  getBookingIDs() {
    return this.filteredBookings?.length > 0 ? this.filteredBookings.map(cull => cull.id) : null;
  }

  getAdvertiserIDs() {
    return this.filteredAdvertisers?.length > 0 ? this.filteredAdvertisers.map(advertiser => advertiser.id) : null;
  }

  getScreenIDs() {
    if (!this.screen || this.screen.length === 0) {
      return null;
    }
    const screens = this.screen
      .split(',')
      .map(screen => Number(screen.trim()))
      .filter(Boolean);
    return screens?.length > 0 ? screens : null;
  }

  getDeviceIDs() {
    const deviceId = this.inDeviceContext();
    if (deviceId != null) {
      return [deviceId];
    }

    if (!this.device || this.device.length === 0) {
      return null;
    }
    const devices = this.device
      .split(',')
      .map(device => Number(device.trim()))
      .filter(Boolean);
    return devices?.length > 0 ? devices : null;
  }

  requestPlayoutDescriptors() {
    if (!this.options?.pagination) {
      return;
    }
    this.loading.set(true);
    const options = this.getRequestOptions();
    const sub = this.api
      .ss_cd_PlayoutDescriptor_retrieveAll(options, this.options.pagination)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (descriptors: PublisherPlayoutDescriptorFullDTO[]) => {
          this.playoutDescriptors = descriptors;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  changeFrom(date: SbLocalDateTime) {
    if (!date) {
      this.from = null;
    } else {
      this.from = new SbDateTime(date.value);
    }
    this.requestDebounced();
  }

  changeTill(date: SbLocalDateTime) {
    if (!date) {
      this.till = null;
    } else {
      this.till = new SbDateTime(date.value);
    }
    this.requestDebounced();
  }

  update($event: any[], type: string) {
    switch (type) {
      case 'BOOKINGS':
        if ($event?.length > 0) {
          this.filteredBookings = [...$event];
        } else {
          this.filteredBookings = null;
        }
        break;
      case 'MEDIA':
        if ($event?.length > 0) {
          this.filteredMedia = [...$event];
        } else {
          this.filteredMedia = [];
        }
        break;
      case 'ADVERTISERS':
        if ($event?.length > 0) {
          this.filteredAdvertisers = [...$event];
        } else {
          this.filteredAdvertisers = [];
        }
        break;
    }
    this.requestDebounced();
  }

  msRequest(type: string, options?: MsOptions) {
    switch (type) {
      case 'MEDIA':
        if (options) {
          this.msMediaOptions = options;
        }
        if (this.msMediaOptions?.pagination) {
          this.api
            .mm_Medium_retrieveAll(
              {
                search: this.util.encodeSearchQueryParam(false, this.msMediaOptions?.search),
                locked: true,
              },
              this.msMediaOptions.pagination
            )
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (media: MediumFullDTO[]) => {
                this.media = media;
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
        break;
      case 'BOOKINGS':
        if (options) {
          this.msBookingsOptions = options;
        }
        if (this.msBookingsOptions?.pagination) {
          this.api
            .ds_cm_Booking_retrieveAll(
              {
                search: this.util.encodeSearchQueryParam(false, this.msBookingsOptions?.search),
              },
              this.msBookingsOptions.pagination
            )
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (bookings: BookingDTO[]) => {
                this.bookings = bookings;
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
        break;
      case 'ADVERTISERS':
        if (options) {
          this.msAdvertiserOptions = options;
        }
        if (this.msAdvertiserOptions?.pagination) {
          this.api
            .shared_Client_retrieveConnectedClients(
              {
                search: this.util.encodeSearchQueryParam(false, this.msAdvertiserOptions?.search),
              },
              this.msAdvertiserOptions.pagination
            )
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (retrievedClients: ClientDTO[]) => {
                this.advertisers = retrievedClients;
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
        break;
    }
  }

  changeAggregationUsage($event: boolean) {
    this.useAggregated = $event;

    this.loading.set(false);
    this.requestDebounced();
  }

  protected export(withDetails: boolean) {
    this.loading.set(true);
    const options = this.getRequestOptions();
    iif(
      () => withDetails,
      this.api.ss_cd_PlayoutDescriptor_exportCSVWithFullDetails(options),
      this.api.ss_cd_PlayoutDescriptor_exportCSV(options)
    )
      .pipe(
        take(1),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (fileLink: FileLink) => {
          if (fileLink) {
            window.open(fileLink.toString(), 'Download');
          }
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  private getRequestOptions() {
    return {
      productive: this.productive !== null ? this.productive : null,
      onAirFrom: this.from ? this.from : null,
      onAirTill: this.till ? this.till : null,
      advertiser: this.getAdvertiserIDs(),
      screen: this.getScreenIDs(),
      device: this.getDeviceIDs(),
      booking: this.getBookingIDs(),
      medium: this.getMediaIDs(),
    };
  }

  protected exportPlayouts(descriptor: PublisherPlayoutDescriptorFullDTO) {
    if (!descriptor) {
      return;
    }
    const exportOptions: ExportModalOptions = {
      kind: 'DESCRIPTOR',
      id: descriptor.id,
    };
    this.modalService
      .showNavigationModal({
        entryComponent: {
          value: SbExportModalComponent,
          data: exportOptions,
        },
        title: this.translate.instant('lang.globals.exportPlayoutsForDescriptor'),
        modalOptions: { size: 'lg' },
      })
      .then(() => {})
      .catch(() => {});
  }

  protected setProductive($event: number) {
    this.productive = $event === 1 ? true : $event === 0 ? false : null;
    this.requestDebounced();
  }
}
